// 断点续传上传管理器
// - IndexedDB 队列持久化 (刷新/断网后 recover)
// - 网断自动暂停 → 恢复自动续传
// - 刷新后 recover() 扫描残留继续
// 注: 本地 wrangler pages dev 用单 PUT (miniflare 不支持 Multipart);
//     生产线上后端 [[path]].js 会切到 R2 Multipart (分帧续传真生效)。

import {
  queueUpload, getAllQueued, updateQueued, deleteQueued, clearDoneQueue,
  type QueuedUpload,
} from './db';

const PART_SIZE = 5 * 1024 * 1024; // 5MB 分片

export interface UploadEvent {
  id: string;
  filename: string;
  progress: number;      // 0..1
  status: QueuedUpload['status'];
  byteProgress: number;  // 已传字节
  size: number;
  message?: string;
}

type Listener = (e: UploadEvent) => void;

class UploadManager {
  private listeners = new Set<Listener>();
  private active = new Set<string>();   // 正在上传的队列 id
  private offline = false;
  private recovering = false;

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  private emit(e: UploadEvent) {
    for (const l of this.listeners) l(e);
  }

  // 网断/上线监听
  bindNetwork() {
    window.addEventListener('offline', () => { this.offline = true; this.pauseAll('offline'); });
    window.addEventListener('online', () => { this.offline = false; this.resumeAll(); });
    window.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // 切后台: 不强制暂停, 但标记 background (失败会暂停)
      } else {
        this.resumeAll();
      }
    });
  }

  // App 启动: 恢复残留队列
  async recover() {
    if (this.recovering) return;
    this.recovering = true;
    await clearDoneQueue();
    const pending = await getAllQueued();
    for (const q of pending) {
      if (q.status === 'queued' || q.status === 'uploading' || q.status === 'paused' || q.status === 'failed') {
        await updateQueued(q.id, { status: 'queued', pausedReason: 'refresh' });
      }
    }
    this.recovering = false;
    await this.resumeAll();
  }

  // 加入队列
  async add(file: File, meta: { takenAt?: number; width?: number; height?: number; duration?: number } = {}): Promise<string> {
    const id = crypto.randomUUID();
    const item: QueuedUpload = {
      id,
      fileBlob: file,
      filename: file.name,
      size: file.size,
      mime: file.type || 'application/octet-stream',
      totalParts: Math.max(1, Math.ceil(file.size / PART_SIZE)),
      completedParts: [],
      status: 'queued',
      retries: 0,
      createdAt: Date.now(),
      ...meta,
    };
    await queueUpload(item);
    this.emit({ id, filename: file.name, progress: 0, status: 'queued', byteProgress: 0, size: file.size });
    await this.tick(id);
    return id;
  }

  pauseAll(reason: QueuedUpload['pausedReason']) {
    for (const id of this.active) {
      updateQueued(id, { status: 'paused', pausedReason: reason });
    }
    // TODO: 真正 abort XHR 需要 AbortController, 简化版靠 status 标记
    this.active.clear();
    this.emit({ id: '', filename: '', progress: 0, status: 'paused', byteProgress: 0, size: 0, message: reason });
  }

  resumeAll() {
    this.recover();
  }

  async retry(id: string) {
    await updateQueued(id, { status: 'queued', retries: 0, pausedReason: undefined });
    await this.tick(id);
  }

  async pause(id: string) {
    await updateQueued(id, { status: 'paused', pausedReason: 'user' });
    this.active.delete(id);
  }

  remove(id: string) {
    deleteQueued(id);
  }

  // 处理单个队列项
  private async tick(id: string): Promise<void> {
    if (this.active.has(id)) return;
    if (this.offline) return;

    const item = await getAllQueued().then((all) => all.find((q) => q.id === id));
    if (!item || item.status === 'done' || item.status === 'paused') {
      // paused 由用户触发, 不自动继续
      return;
    }

    this.active.add(id);
    await updateQueued(id, { status: 'uploading' });

    try {
      // Worker 中转上传 (写 r2data APAC 桶, 数据在亚太)
      const form = new FormData();
      form.append('file', item.fileBlob, item.filename);
      form.append('filename', item.filename);
      form.append('mime', item.mime);
      form.append('displayName', item.filename);
      if (item.width) form.append('width', String(item.width));
      if (item.height) form.append('height', String(item.height));
      if (item.duration) form.append('duration', String(item.duration));
      if (item.takenAt) form.append('takenAt', String(item.takenAt));

      const xhr = new XMLHttpRequest();
      let lastEmit = 0;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const now = Date.now();
          if (now - lastEmit > 200) {
            lastEmit = now;
            this.emit({ id, filename: item.filename, progress: e.loaded / e.total, status: 'uploading', byteProgress: e.loaded, size: e.total });
          }
        }
      };
      xhr.onload = async () => {
        try {
          if (xhr.status >= 200 && xhr.status < 300) {
            await updateQueued(id, { status: 'done', completedParts: Array.from({ length: item.totalParts }, (_, i) => i + 1) });
            this.emit({ id, filename: item.filename, progress: 1, status: 'done', byteProgress: item.size, size: item.size });
          } else {
            throw new Error('HTTP ' + xhr.status);
          }
        } catch (e: any) {
          await this.fail(id, item, e);
        } finally {
          this.active.delete(id);
        }
      };
      xhr.onerror = async () => {
        if (this.offline) { await updateQueued(id, { status: 'paused', pausedReason: 'offline' }); }
        else { await this.fail(id, item, new Error('Network error')); }
        this.active.delete(id);
      };
      xhr.open('POST', '/api/upload');
      xhr.withCredentials = true;
      xhr.send(form);
    } catch (e: any) {
      await this.fail(id, item, e);
      this.active.delete(id);
    }
  }

  private async fail(id: string, item: QueuedUpload, e: Error) {
    if (item.retries < 3) {
      await updateQueued(id, { status: 'queued', retries: item.retries + 1 });
      this.emit({ id, filename: item.filename, progress: 0, status: 'queued', byteProgress: 0, size: item.size, message: 'retry ' + (item.retries + 1) });
      setTimeout(() => this.tick(id), 1000 * (item.retries + 1));
    } else {
      await updateQueued(id, { status: 'failed' });
      this.emit({ id, filename: item.filename, progress: 0, status: 'failed', byteProgress: 0, size: item.size, message: e.message });
    }
  }
}

export const uploadManager = new UploadManager();